import {
  AlertCircleIcon,
  Cancel01Icon,
  CheckmarkCircle02Icon,
  InformationCircleIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';

import styles from './FormMessage.module.scss';

export const MessageType = {
  Success: 'Success',
  Error: 'Error',
  Info: 'Info',
  Warning: 'Warning',
  Neutral: 'Neutral',
  Brand: 'Brand',
};

export const FormMessage = ({
  message = '',
  type = MessageType.Error,
  className,
  small = false,
  noBackground = false,
  onClose = () => {},
  closeable = true,
  noMargin = false,
  noIcon = false,
  maxWidthContent = false,
  noBorder = false,
  fontClass = '',
  rounded = '2xl',
  dataTestId,
}) => {
  if (message == null || (typeof message === 'string' && message.trim() === '')) return null;

  const isStringMessage = typeof message === 'string';

  const noBackgroundColor =
    noBackground &&
    {
      [MessageType.Error]: 'red',
      [MessageType.Success]: 'green',
      [MessageType.Warning]: 'orange',
      [MessageType.Info]: 'blue',
      [MessageType.Neutral]: 'blue',
      [MessageType.Brand]: 'brand',
    }[type];

  return (
    <div
      data-testid={dataTestId}
      className={cn(
        styles.root,
        styles[`root${type}`],
        { [styles.noMargin]: noMargin },
        rounded && `rounded-${rounded}`,
        { [styles.small]: small },
        { [styles.noBackground]: noBackground },
        noBackgroundColor && `border border-${noBackgroundColor}-1`,
        { [styles.maxWidthContent]: maxWidthContent },
        { [styles.noBorder]: noBorder },
        className,
      )}>
      <div className="d-flex w-full min-w-0 align-items-center">
        {!noIcon && (
          <span className={styles.iconWrapper}>
            {type === MessageType.Error && (
              <HugeiconsIcon icon={AlertCircleIcon} className="text-red-2" size="20" />
            )}
            {type === MessageType.Success && (
              <HugeiconsIcon icon={CheckmarkCircle02Icon} className="text-green-2" size="20" />
            )}
            {(type === MessageType.Info || type === MessageType.Neutral) && (
              <HugeiconsIcon icon={InformationCircleIcon} className="text-blue-2" size="20" />
            )}
            {type === MessageType.Warning && (
              <HugeiconsIcon icon={AlertCircleIcon} className="text-orange-2" size="20" />
            )}
            {type === MessageType.Brand && (
              <HugeiconsIcon icon={InformationCircleIcon} className="text-primary-4" size="20" />
            )}
          </span>
        )}
        {isStringMessage ? (
          <p
            className={cn(
              styles.message,
              { [styles.noIconMessage]: noIcon },
              noBackgroundColor && `text-${noBackgroundColor}-3`,
              fontClass,
            )}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message) }}
          />
        ) : (
          <div
            className={cn(
              styles.message,
              { [styles.noIconMessage]: noIcon },
              noBackgroundColor && `text-${noBackgroundColor}-3`,
              fontClass,
            )}>
            {message}
          </div>
        )}
      </div>
      {closeable && <HugeiconsIcon icon={Cancel01Icon} onClick={onClose} type={type} size="20" />}
    </div>
  );
};

FormMessage.propTypes = {
  /** String (HTML) or React node (e.g. JSX). Strings are rendered with dangerouslySetInnerHTML. */
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  type: PropTypes.oneOf([
    MessageType.Success,
    MessageType.Error,
    MessageType.Info,
    MessageType.Warning,
    MessageType.Neutral,
  ]),
  className: PropTypes.string,
  onClose: PropTypes.func,
};
